// src/components/Radiologist/QAQueue.jsx
// QA review queue — shows preliminary reports awaiting final approval
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Grid,
} from '@mui/material';
import {
  CheckCircle,
  Assignment as ReturnIcon,
  Refresh,
  RateReview as PrelimIcon,
  Flag,
  Edit,
  ChatBubbleOutline as ChatIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getQaQueue, approveDycovueReport, qaRejectDycovueReport } from '../../services/reports';
import { formatPatientName } from '../../services/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';

function QAQueue({ onStudyClick, onEditReport }) {
  const { user: currentUser } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#64748B';
  const borderColor = isDark ? '#334155' : '#E2E8F0';
  const inputBg = isDark ? '#121212' : '#F8FAFC';

  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Return-for-revisions dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [qaComments, setQaComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Approve / Resolve confirmation modal state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveTargetReport, setApproveTargetReport] = useState(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getQaQueue();
      const rawList = Array.isArray(data.reports) ? data.reports : [];
      // Deduplicate by study, preferring active working copies
      const studyMap = new Map();
      for (const r of rawList) {
        const studyId = (r.study?._id || r.study)?.toString() || r._id.toString();
        if (!studyMap.has(studyId)) {
          studyMap.set(studyId, r);
        } else {
          const existing = studyMap.get(studyId);
          if (r.versionType === 'working' && existing.versionType !== 'working') {
            studyMap.set(studyId, r);
          }
        }
      }
      setReports(Array.from(studyMap.values()));
    } catch (err) {
      setError('Failed to load QA queue');
    } finally {
      setLoading(false);
    }
  }, []);

  const paginatedReports = React.useMemo(() => {
    return reports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [reports, page, rowsPerPage]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const openApproveDialog = (report) => {
    setApproveTargetReport(report);
    setActionError('');
    setApproveDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!approveTargetReport) return;
    setActionLoading(true);
    setActionError('');
    try {
      await approveDycovueReport(approveTargetReport._id);
      setApproveDialogOpen(false);
      setApproveTargetReport(null);
      await loadQueue();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to approve report');
    } finally {
      setActionLoading(false);
    }
  };

  const openReturnDialog = (report) => {
    setSelectedReport(report);
    setQaComments('');
    setActionError('');
    setReturnDialogOpen(true);
  };

  const handleReturnForRevisions = async () => {
    if (!qaComments.trim()) {
      setActionError('Please provide feedback comments for the radiologist');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await qaRejectDycovueReport(selectedReport._id, qaComments.trim());
      setReturnDialogOpen(false);
      setSelectedReport(null);
      await loadQueue();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to return report');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ px: 1.5, py: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Flag color="error" sx={{ fontSize: 14 }} />
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.72rem' }}>
            {reports.length} report{reports.length !== 1 ? 's' : ''} in QA &amp; revision queue
          </Typography>
        </Stack>
        <Button size="small" startIcon={<Refresh sx={{ fontSize: 13 }} />} onClick={loadQueue} disabled={loading} sx={{ height: 24, fontSize: '0.68rem', py: 0, px: 1, textTransform: 'none' }}>
          Refresh
        </Button>
      </Box>

      {reports.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle color="success" sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
          <Typography color="textSecondary" sx={{ fontSize: '0.75rem' }}>No reports currently awaiting QA or revision</Typography>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ px: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { py: '2px !important', px: '6px !important', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottomColor: borderColor } }}>
                  <TableCell sx={{ color: textColorSecondary }}>Patient</TableCell>
                  <TableCell sx={{ color: textColorSecondary }}>Modality</TableCell>
                  <TableCell sx={{ color: textColorSecondary }}>Status</TableCell>
                  <TableCell sx={{ color: textColorSecondary }}>Description &amp; Feedback</TableCell>
                  <TableCell sx={{ color: textColorSecondary }}>Radiologist</TableCell>
                  <TableCell sx={{ color: textColorSecondary }}>Submitted</TableCell>
                  <TableCell sx={{ color: textColorSecondary }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReports.map((report) => {
                  const isFlagged = report.facilityReview?.status === 'flagged';
                  const flaggedBy = report.facilityReview?.flaggedBy;

                  return (
                    <TableRow
                      key={report._id}
                      hover
                      sx={{
                        '& td': { py: '1.5px !important', px: '6px !important', fontSize: '0.72rem', height: '26px' },
                        bgcolor: isFlagged
                          ? (isDark ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.04)')
                          : 'inherit',
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.04) !important' : undefined
                        }
                      }}
                    >
                    <TableCell sx={{ borderBottomColor: borderColor }}>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160, fontSize: '0.72rem', color: textColorPrimary }}>
                        {formatPatientName(report.study?.patientName) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottomColor: borderColor }}>
                      <Chip
                        label={report.study?.modality || report.modality || '—'}
                        size="small"
                        color={isFlagged ? 'error' : 'warning'}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.62rem', px: '2px' }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottomColor: borderColor }}>
                      {isFlagged ? (
                        <Chip
                          label="FLAGGED"
                          color="error"
                          size="small"
                          icon={<Flag sx={{ fontSize: 11 }} />}
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, px: '2px' }}
                        />
                      ) : (
                        <Chip
                          label={report.status?.toUpperCase() || 'PRELIMINARY'}
                          color="warning"
                          size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, px: '2px' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ borderBottomColor: borderColor }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180, fontWeight: 500, fontSize: '0.72rem', color: textColorPrimary }}>
                        {report.study?.studyDescription || '—'}
                      </Typography>
                      {isFlagged && report.facilityReview?.concerns && (
                        <Box sx={{ mt: 0.5, p: 0.5, borderRadius: '4px', bgcolor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)', borderLeft: '3px solid #EF4444' }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#F87171' : '#DC2626', fontWeight: 700, display: 'block', fontSize: '0.66rem' }}>
                            {report.facilityName || report.facilityReview?.facilityName || report.study?.facilityName || report.study?.facility?.facilityName || report.study?.institutionName || 'Hospital'} Concerns:
                          </Typography>
                          <Typography variant="caption" sx={{ color: textColorPrimary, whiteSpace: 'pre-wrap', fontSize: '0.68rem' }}>
                            {report.facilityReview.concerns}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ borderBottomColor: borderColor }}>
                      <Typography variant="caption" sx={{ color: textColorSecondary, fontSize: '0.68rem' }}>
                        {report.createdBy
                          ? `${report.createdBy.firstName || ''} ${report.createdBy.lastName || ''}`.trim()
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottomColor: borderColor }}>
                      <Typography variant="caption" sx={{ color: textColorSecondary, whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                        {report.submittedAt || report.facilityReview?.flaggedAt
                          ? new Date(report.submittedAt || report.facilityReview?.flaggedAt).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: '2-digit',
                            })
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottomColor: borderColor }}>
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5} alignItems="center">
                        {isFlagged ? (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<Edit sx={{ fontSize: 12 }} />}
                              onClick={() => {
                                if (onEditReport) {
                                  onEditReport(report.study, report);
                                } else if (onStudyClick) {
                                  onStudyClick(report.study, report);
                                }
                              }}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.68rem',
                                height: '22px',
                                py: 0,
                                px: 0.8,
                                borderRadius: '4px',
                                fontWeight: 700,
                                bgcolor: '#EF4444',
                                '&:hover': {
                                  bgcolor: '#DC2626'
                                }
                              }}
                            >
                              Edit &amp; Revise
                            </Button>
                            {(() => {
                              const currentUserId = currentUser?._id?.toString();
                              const flaggerId = (flaggedBy?._id || flaggedBy)?.toString();
                              const radiologistUser = report.createdBy || report.assignedRadiologist || report.study?.assignedRadiologist;
                              const radiologistId = (radiologistUser?._id || radiologistUser)?.toString();

                              const isCurrentUserFlagger = Boolean(currentUserId && flaggerId && currentUserId === flaggerId);

                              let targetUser = null;
                              let chatTooltip = 'Message Reviewer';

                              if (isCurrentUserFlagger) {
                                targetUser = typeof radiologistUser === 'object' && radiologistUser !== null
                                  ? radiologistUser
                                  : (radiologistId ? { _id: radiologistId, firstName: 'Radiologist', lastName: '' } : null);
                                chatTooltip = 'Message Radiologist';
                              } else {
                                targetUser = typeof flaggedBy === 'object' && flaggedBy !== null
                                  ? flaggedBy
                                  : (flaggerId ? { _id: flaggerId, firstName: 'Reviewer', lastName: '' } : null);
                                chatTooltip = 'Message Reviewer';
                              }

                              if (targetUser?._id && currentUserId && targetUser._id.toString() === currentUserId) {
                                if (radiologistId && radiologistId !== currentUserId) {
                                  targetUser = typeof radiologistUser === 'object' ? radiologistUser : { _id: radiologistId, firstName: 'Radiologist', lastName: '' };
                                  chatTooltip = 'Message Radiologist';
                                } else if (flaggerId && flaggerId !== currentUserId) {
                                  targetUser = typeof flaggedBy === 'object' ? flaggedBy : { _id: flaggerId, firstName: 'Reviewer', lastName: '' };
                                  chatTooltip = 'Message Reviewer';
                                } else {
                                  targetUser = null;
                                }
                              }

                              if (!targetUser || !targetUser._id || (currentUserId && targetUser._id.toString() === currentUserId)) {
                                return null;
                              }

                              return (
                                <Tooltip title={chatTooltip}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      window.dispatchEvent(
                                        new CustomEvent('open-direct-message', {
                                          detail: {
                                            user: targetUser,
                                            context: {
                                              type: 'flagged_report',
                                              reportId: report._id,
                                              studyId: report.study?._id || report.study,
                                              studyUID: report.study?.studyInstanceUID || report.studyInstanceUID,
                                              patientName: report.patientName || report.study?.patientName,
                                              modality: report.modality || report.study?.modality,
                                              facilityName: report.facilityName || report.facilityReview?.facilityName || report.study?.facilityName,
                                              concerns: report.facilityReview?.concerns
                                            }
                                          }
                                        })
                                      );
                                    }}
                                    sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)', '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.2)' } }}
                                  >
                                    <ChatIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              );
                            })()}
                            <Tooltip title="Resolve &amp; Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openApproveDialog(report)}
                                disabled={actionLoading}
                                sx={{
                                  width: 22,
                                  height: 22,
                                  p: 0,
                                  bgcolor: isDark ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
                                  '&:hover': {
                                    bgcolor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(16, 185, 129, 0.18)',
                                  }
                                }}
                              >
                                <CheckCircle sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Approve Final — finalize, generate PDF, notify referring physician">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => openApproveDialog(report)}
                                  disabled={actionLoading}
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    p: 0,
                                    bgcolor: isDark ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
                                    '&:hover': {
                                      bgcolor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(16, 185, 129, 0.18)',
                                    }
                                  }}
                                >
                                  <CheckCircle sx={{ fontSize: 14 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Return for revisions — send back to radiologist with comments">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => openReturnDialog(report)}
                                  disabled={actionLoading}
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    p: 0,
                                    bgcolor: isDark ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.08)',
                                    '&:hover': {
                                      bgcolor: isDark ? 'rgba(245, 158, 11, 0.22)' : 'rgba(245, 158, 11, 0.18)',
                                    }
                                  }}
                                >
                                  <ReturnIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 25, 50, 100]}
          component="div"
          count={reports.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${borderColor}`,
            minHeight: '28px',
            '& .MuiTablePagination-toolbar': {
              minHeight: '28px',
              px: { xs: 1, sm: 2 },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.7rem',
            },
          }}
        />
      </>
      )}

      {/* Resolve & Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !actionLoading && setApproveDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '640px', md: '720px' },
            maxWidth: '740px !important',
            maxHeight: '88vh',
            bgcolor: cardBg,
            color: textColorPrimary,
            borderRadius: '14px',
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? '0 20px 60px rgba(0,0,0,0.85)'
              : '0 20px 60px rgba(0,0,0,0.15)',
            backgroundImage: 'none',
          }
        }}
      >
        {approveTargetReport && (() => {
          const isFlagged = approveTargetReport.facilityReview?.status === 'flagged';
          const concerns = approveTargetReport.facilityReview?.concerns;
          const facilityName = approveTargetReport.facilityName || approveTargetReport.study?.facility?.facilityName || 'Hospital';
          const patientName = formatPatientName(approveTargetReport.study?.patientName) || 'Anonymized';
          const modality = approveTargetReport.study?.modality || approveTargetReport.modality || '—';
          const studyDesc = approveTargetReport.study?.studyDescription || '—';
          const author = approveTargetReport.createdBy
            ? `${approveTargetReport.createdBy.firstName || ''} ${approveTargetReport.createdBy.lastName || ''}`.trim()
            : '—';

          return (
            <>
              {/* Header */}
              <DialogTitle
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 1.25,
                  borderBottom: `1px solid ${borderColor}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ECFDF5',
                      border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.3)' : '#A7F3D0'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#10B981',
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: '"Satoshi", sans-serif',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        color: textColorPrimary,
                        lineHeight: 1.2,
                      }}
                    >
                      {isFlagged ? 'Resolve & Approve Report' : 'Confirm Final Approval'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: textColorSecondary,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }}
                    >
                      {isFlagged
                        ? 'Confirm resolution of hospital concerns and publish final report'
                        : 'Finalize report findings and notify referring facility'}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => !actionLoading && setApproveDialogOpen(false)}
                  disabled={actionLoading}
                  sx={{
                    color: textColorSecondary,
                    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </DialogTitle>

              {/* Body */}
              <DialogContent sx={{ px: 2.5, py: 1.75 }}>
                {actionError && (
                  <Alert severity="error" sx={{ mb: 1.5, py: 0.5, borderRadius: '8px' }}>
                    {actionError}
                  </Alert>
                )}

                {/* Patient & Study Summary Card */}
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC',
                    border: `1px solid ${borderColor}`,
                    mb: 1.5,
                  }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={7}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: textColorPrimary }}>
                          {patientName}
                        </Typography>
                        <Chip
                          label={modality}
                          size="small"
                          color={isFlagged ? 'error' : 'primary'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                        />
                        {isFlagged ? (
                          <Chip
                            label="FLAGGED"
                            color="error"
                            size="small"
                            icon={<Flag sx={{ fontSize: 12 }} />}
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 800 }}
                          />
                        ) : (
                          <Chip
                            label={approveTargetReport.status?.toUpperCase() || 'PRELIMINARY'}
                            color="warning"
                            size="small"
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.8rem', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        <strong>Description:</strong> {studyDesc}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={5} sx={{ textAlign: { sm: 'right' } }}>
                      <Typography variant="caption" sx={{ color: textColorSecondary, display: 'block' }}>
                        <strong>Radiologist:</strong> {author}
                      </Typography>
                      {approveTargetReport.submittedAt && (
                        <Typography variant="caption" sx={{ color: textColorSecondary }}>
                          {new Date(approveTargetReport.submittedAt).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                {/* Hospital Concerns / Feedback (if flagged) */}
                {isFlagged && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '10px',
                      bgcolor: isDark ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2',
                      border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.25)' : '#FCA5A5'}`,
                      borderLeft: '4px solid #EF4444',
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? '#F87171' : '#DC2626',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <Flag sx={{ fontSize: 14 }} /> {facilityName} Concerns / Feedback:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? '#FCA5A5' : '#991B1B',
                        fontWeight: 500,
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.82rem',
                      }}
                    >
                      {concerns || 'Please review and update the findings for hospital re-review.'}
                    </Typography>
                  </Box>
                )}

                {/* Outcome summary text */}
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: '8px',
                    bgcolor: isDark ? 'rgba(16, 185, 129, 0.08)' : '#F0FDF4',
                    border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.2)' : '#BBF7D0'}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#34D399' : '#15803D',
                      fontWeight: 600,
                      display: 'block',
                      lineHeight: 1.4,
                      fontSize: '0.78rem',
                    }}
                  >
                    {isFlagged
                      ? `✓ Resolving will clear the flag, synchronize approved findings to the original study report, generate the official PDF, and notify ${facilityName} that the revised report is approved.`
                      : '✓ Approving will finalize report findings, generate the signed PDF document, and notify the referring clinician.'}
                  </Typography>
                </Box>
              </DialogContent>

              {/* Actions */}
              <DialogActions
                sx={{
                  px: 2.5,
                  py: 1.25,
                  borderTop: `1px solid ${borderColor}`,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1.5,
                }}
              >
                <Button
                  onClick={() => setApproveDialogOpen(false)}
                  disabled={actionLoading}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: textColorSecondary,
                    px: 2,
                    fontSize: '0.82rem',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmApprove}
                  disabled={actionLoading}
                  size="small"
                  startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: '#10B981',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2.5,
                    py: 0.65,
                    borderRadius: '8px',
                    fontSize: '0.82rem',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                    '&:hover': {
                      bgcolor: '#059669',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    }
                  }}
                >
                  {actionLoading
                    ? 'Approving...'
                    : (isFlagged ? 'Resolve & Approve' : 'Confirm Approval')}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Return for Revisions Dialog */}
      <Dialog
        open={returnDialogOpen}
        onClose={() => !actionLoading && setReturnDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '520px' },
            maxWidth: '540px !important',
            maxHeight: '85vh',
            bgcolor: cardBg,
            color: textColorPrimary,
            borderRadius: '14px',
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? '0 20px 60px rgba(0,0,0,0.85)'
              : '0 20px 60px rgba(0,0,0,0.15)',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.25,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7',
                border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.3)' : '#FDE68A'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#D97706',
              }}
            >
              <ReturnIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Satoshi", sans-serif',
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  color: textColorPrimary,
                  lineHeight: 1.2,
                }}
              >
                Return Report for Revisions
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: textColorSecondary,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                Send report back to radiologist with feedback
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => !actionLoading && setReturnDialogOpen(false)}
            disabled={actionLoading}
            sx={{
              color: textColorSecondary,
              '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.75 }}>
          {actionError && (
            <Alert severity="error" sx={{ mb: 1.5, py: 0.5, borderRadius: '8px' }}>
              {actionError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ color: textColorSecondary, mb: 1.5, fontSize: '0.82rem' }}>
            Provide clear feedback explaining what findings or sections need to be revised.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="QA Comments"
            placeholder="Describe what needs to be revised or corrected..."
            value={qaComments}
            onChange={(e) => setQaComments(e.target.value)}
            disabled={actionLoading}
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: inputBg,
                color: textColorPrimary,
                borderRadius: '8px',
                fontSize: '0.85rem',
              }
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: `1px solid ${borderColor}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setReturnDialogOpen(false)}
            disabled={actionLoading}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: textColorSecondary,
              px: 2,
              fontSize: '0.82rem',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleReturnForRevisions}
            disabled={actionLoading || !qaComments.trim()}
            size="small"
            startIcon={actionLoading ? <CircularProgress size={16} /> : <ReturnIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              py: 0.65,
              borderRadius: '8px',
              fontSize: '0.82rem',
            }}
          >
            Return for Revisions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QAQueue;
